import { Component, inject, signal, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FeatureTourService } from '../../core/services/feature-tour.service';
import { OnboardingService } from '../../core/services/onboarding.service';

interface SpotlightRect {
  x: number; y: number; width: number; height: number;
}

@Component({
  selector: 'sw-feature-tour',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './feature-tour.component.html',
  styleUrl: './feature-tour.component.scss'
})
export class FeatureTourComponent implements AfterViewChecked {
  readonly tourService = inject(FeatureTourService);
  readonly onboarding = inject(OnboardingService);

  readonly spotlightRect = signal<SpotlightRect | null>(null);
  readonly bubblePos = signal<{ top: string; left: string; transform: string }>({ top: '50%', left: '50%', transform: 'translate(-50%,-50%)' });

  ngAfterViewChecked(): void {
    this.updateSpotlight();
  }

  private updateSpotlight(): void {
    const step = this.tourService.currentStep();
    if (!step || !step.targetSelector) {
      this.spotlightRect.set(null);
      this.bubblePos.set({ top: '50%', left: '50%', transform: 'translate(-50%,-50%)' });
      return;
    }
    const el = document.querySelector(step.targetSelector) as HTMLElement;
    if (!el) {
      this.spotlightRect.set(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const pad = 10;
    this.spotlightRect.set({ x: rect.left - pad, y: rect.top - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 });
    this.positionBubble(rect, step.placement ?? 'bottom');
  }

  private positionBubble(rect: DOMRect, placement: string): void {
    const bubbleW = 340;
    const bubbleH = 180;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = 20;
    let top = '', left = '', transform = '';

    switch (placement) {
      case 'bottom':
        top = Math.min(rect.bottom + pad, vh - bubbleH - pad) + 'px';
        left = Math.max(pad, Math.min(rect.left + rect.width / 2, vw - bubbleW - pad)) + 'px';
        transform = 'translateX(-50%)';
        const l = parseFloat(left);
        if (l - bubbleW / 2 < pad) left = (bubbleW / 2 + pad) + 'px';
        if (l + bubbleW / 2 > vw - pad) left = (vw - bubbleW / 2 - pad) + 'px';
        break;
      case 'top':
        top = Math.max(pad, rect.top - bubbleH - pad) + 'px';
        left = (rect.left + rect.width / 2) + 'px';
        transform = 'translateX(-50%)';
        break;
      case 'right':
        top = (rect.top + rect.height / 2) + 'px';
        left = Math.min(rect.right + pad, vw - bubbleW - pad) + 'px';
        transform = 'translateY(-50%)';
        break;
      case 'left':
        top = (rect.top + rect.height / 2) + 'px';
        left = Math.max(pad, rect.left - bubbleW - pad) + 'px';
        transform = 'translateY(-50%)';
        break;
      default:
        top = '50%'; left = '50%'; transform = 'translate(-50%,-50%)';
    }
    this.bubblePos.set({ top, left, transform });
  }

  next(): void { this.tourService.next(); }
  prev(): void { this.tourService.prev(); }
  skip(): void {
    const tour = this.tourService.activeTour();
    if (tour) this.onboarding.completeTour(tour.id);
    this.tourService.end();
  }
}
